import {useState, useEffect} from 'react'
import {useNavigate} from 'react-router-dom'
import {
    Box,
    TextField,
    Button,
    Typography,
    Paper,
    Snackbar,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    CircularProgress
} from '@mui/material'
import {useAccount} from '../context/AccountContext'
import axios from 'axios'

const PLACEHOLDER_PASSWORD = '••••••••'

function AccountSettings() {
    const navigate = useNavigate()
    const {selectedAccountConnection, accounts, isAdminUser, createAccount, updateAccount, deleteAccount} = useAccount()
    
    const [accountName, setAccountName] = useState('')
    const [origamiAccountId, setOrigamiAccountId] = useState<number | null>(null)
    const [apiAccountUsername, setApiAccountUsername] = useState('')
    const [password, setPassword] = useState('')
    const [passwordChanged, setPasswordChanged] = useState(false)
    
    const [errors, setErrors] = useState<{[key: string]: string}>({})
    const [loading, setLoading] = useState(false)
    const [snackbar, setSnackbar] = useState<{open: boolean, message: string, severity: 'success' | 'error'}>({
        open: false,
        message: '',
        severity: 'success'
    })
    
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [deleting, setDeleting] = useState(false)
    
    const isEditMode = selectedAccountConnection !== null && accounts.length > 0

    useEffect(() => {
        if (isEditMode && selectedAccountConnection) {
            setAccountName(selectedAccountConnection.accountName)
            setOrigamiAccountId(selectedAccountConnection.origamiAccountId)
            setApiAccountUsername(selectedAccountConnection.apiUserName || '')
            setPassword(PLACEHOLDER_PASSWORD)
            setPasswordChanged(false)
        } else {
            // Create mode
            setAccountName('')
            setOrigamiAccountId(null)
            setApiAccountUsername('')
            setPassword('')
            setPasswordChanged(false)
        }
    }, [isEditMode, selectedAccountConnection])

    const handlePasswordChange = (value: string) => {
        setPassword(value)
        setPasswordChanged(value !== PLACEHOLDER_PASSWORD)
    }

    const validate = (): boolean => {
        const newErrors: {[key: string]: string} = {}
        
        if (!accountName.trim()) {
            newErrors.accountName = 'Account name is required'
        }
        
        if (!apiAccountUsername.trim()) {
            newErrors.apiAccountUsername = 'API username is required'
        }
        
        if (!isEditMode && !password.trim()) {
            newErrors.password = 'Password is required for new accounts'
        }
        
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async () => {
        if (!validate()) {
            return
        }

        if (!isAdminUser) {
            setSnackbar({open: true, message: 'Only admin users can manage accounts', severity: 'error'})
            return
        }

        setLoading(true)
        
        try {
            if (isEditMode && selectedAccountConnection) {
                // Update mode
                const updateData: any = {
                    id: selectedAccountConnection.id,
                    accountName: accountName.trim(),
                    apiAccountUsername: apiAccountUsername.trim()
                }
                
                // Only include password if it was changed
                if (passwordChanged && password !== PLACEHOLDER_PASSWORD) {
                    updateData.apiAccountPassword = password
                }
                
                await updateAccount(updateData)
                setSnackbar({open: true, message: 'Account updated successfully', severity: 'success'})
            } else {
                // Create mode
                await createAccount({
                    accountName: accountName.trim(),
                    apiAccountUsername: apiAccountUsername.trim(),
                    apiAccountPassword: password
                })
                setSnackbar({open: true, message: 'Account created successfully', severity: 'success'})
                navigate('/')
            }
        } catch (error: any) {
            console.error('Failed to save account:', error)
            setSnackbar({
                open: true,
                message: error.response?.data?.message || error.message || 'Failed to save account',
                severity: 'error'
            })
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = async () => {
        if (!selectedAccountConnection) {
            return
        }

        setDeleting(true)
        
        try {
            // First, delete all versions associated with this account
            try {
                await axios.post('/api/admin/versions/delete', {allPolicySets: true})
            } catch (versionError) {
                console.error('Failed to delete versions:', versionError)
                throw new Error('Failed to delete version data. Account deletion aborted.')
            }
            
            // Then delete the account
            await deleteAccount(selectedAccountConnection.id)
            
            setSnackbar({open: true, message: 'Account and version data deleted successfully', severity: 'success'})
            setDeleteDialogOpen(false)
            navigate('/')
        } catch (error: any) {
            console.error('Failed to delete account:', error)
            setSnackbar({
                open: true,
                message: error.message || error.response?.data?.message || 'Failed to delete account',
                severity: 'error'
            })
        } finally {
            setDeleting(false)
        }
    }

    return (
        <Box sx={{maxWidth: 800, margin: '0 auto', padding: 3}}>
            <Paper elevation={3} sx={{padding: 4}}>
                <Typography variant="h4" gutterBottom>
                    {isEditMode ? 'Account Settings' : 'Create Account'}
                </Typography>
                
                {!isEditMode && (
                    <Alert severity="info" sx={{marginBottom: 3}}>
                        No account configured. Please create one to continue.
                    </Alert>
                )}
                
                <Box component="form" sx={{display: 'flex', flexDirection: 'column', gap: 3, marginTop: 3}}>
                    <TextField
                        label="Account Name"
                        value={accountName}
                        onChange={(e) => setAccountName(e.target.value)}
                        error={!!errors.accountName}
                        helperText={errors.accountName}
                        required
                        fullWidth
                        disabled={loading}
                    />
                    
                    {isEditMode && origamiAccountId !== null && (
                        <TextField
                            label="Origami Account ID"
                            value={origamiAccountId}
                            disabled
                            fullWidth
                            helperText="This field is read-only and set by the system"
                        />
                    )}
                    
                    <TextField
                        label="API Username"
                        value={apiAccountUsername}
                        onChange={(e) => setApiAccountUsername(e.target.value)}
                        error={!!errors.apiAccountUsername}
                        helperText={errors.apiAccountUsername}
                        required
                        fullWidth
                        disabled={loading}
                    />
                    
                    <TextField
                        label="API Password"
                        type="password"
                        value={password}
                        onChange={(e) => handlePasswordChange(e.target.value)}
                        error={!!errors.password}
                        helperText={isEditMode 
                            ? (errors.password || 'Password will only be updated if you enter a new value.') 
                            : (errors.password || 'Required for new accounts')}
                        required={!isEditMode}
                        fullWidth
                        disabled={loading}
                    />
                    
                    <Box sx={{display: 'flex', gap: 2, marginTop: 2}}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSubmit}
                            disabled={loading}
                            sx={{flex: 1}}
                        >
                            {loading ? <CircularProgress size={24} /> : (isEditMode ? 'Update Account' : 'Create Account')}
                        </Button>
                        
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => navigate('/')}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                    </Box>
                    
                    {isEditMode && (
                        <Box sx={{marginTop: 4, paddingTop: 3, borderTop: '1px solid #e0e0e0'}}>
                            <Typography variant="h6" color="error" gutterBottom>
                                Danger Zone
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{marginBottom: 2}}>
                                Deleting your account will remove all account data and associated version data. This action cannot be undone.
                            </Typography>
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={() => setDeleteDialogOpen(true)}
                                disabled={loading}
                                sx={{color: 'error.main', borderColor: 'error.main', '&:hover': {borderColor: 'error.dark', backgroundColor: 'error.light'}}}
                            >
                                Delete Account
                            </Button>
                        </Box>
                    )}
                </Box>
            </Paper>
            
            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => !deleting && setDeleteDialogOpen(false)}
            >
                <DialogTitle>Confirm Account Deletion</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will permanently delete the account and all associated version data. 
                        This action cannot be undone.
                        <br /><br />
                        Are you sure you want to continue?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} color="secondary" disabled={deleting} sx={{color: 'error.main'}}>
                        {deleting ? <CircularProgress size={24} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({...snackbar, open: false})}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({...snackbar, open: false})}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    )
}

export default AccountSettings
